import { useState } from "react";

interface StarAuthModalProps {
  mode: "login" | "signup";
  onClose: () => void;
  onSuccess: (user: StarUser) => void;
}

export interface StarUser {
  id: string;
  name: string;
  email: string;
  phone: string;
  platform: string;
  platformHandle: string;
  followers: string;
  country: string;
  bio: string;
  photo: string;
  commissionRate: number;
  verified: boolean;
  joinedDate: string;
  totalEarned: number;
  totalSold: number;
}

const platformOptions = ["YouTube", "TikTok", "Facebook", "Twitter/X"];
const platformIcons: Record<string, string> = {
  YouTube: "ri-youtube-line",
  TikTok: "ri-tiktok-line",
  Facebook: "ri-facebook-circle-line",
  "Twitter/X": "ri-twitter-x-line",
};

const mockAccounts: StarUser[] = [
  {
    id: "star-001",
    name: "Alex Rivera",
    email: "alex@example.com",
    phone: "+1 555 000 0001",
    platform: "YouTube",
    platformHandle: "@AlexRiveraTech",
    followers: "4.2M",
    country: "USA",
    bio: "Tech reviewer & gadget enthusiast. I help my audience find the best products at the best prices. Partnered with CrownGlobal for dropshipping.",
    photo: "https://public.readdy.ai/ai/img_res/8a3ed94895bc39afe62f07a019f7056d.jpg",
    commissionRate: 15,
    verified: true,
    joinedDate: "Jan 2025",
    totalEarned: 14763,
    totalSold: 3842,
  },
  {
    id: "star-002",
    name: "Mia Chen",
    email: "mia@example.com",
    phone: "+86 138 0000 0002",
    platform: "TikTok",
    platformHandle: "@MiaChenBeauty",
    followers: "8.7M",
    country: "China",
    bio: "Beauty & skincare creator. I share honest reviews and help my followers discover amazing products from around the world.",
    photo: "https://readdy.ai/api/search-image?query=young%20female%20fashion%20influencer%20content%20creator%20professional%20portrait%20clean%20studio%20background%20stylish%20modern%20look&width=300&height=300&seq=star2&orientation=squarish",
    commissionRate: 20,
    verified: true,
    joinedDate: "Feb 2025",
    totalEarned: 37500,
    totalSold: 9210,
  },
];

type LoginMethod = "email" | "phone";

export default function StarAuthModal({ mode: initialMode, onClose, onSuccess }: StarAuthModalProps) {
  const [mode, setMode] = useState(initialMode);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loginMethod, setLoginMethod] = useState<LoginMethod>("email");

  // Login fields
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPhone, setLoginPhone] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Signup fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [signupMethod, setSignupMethod] = useState<LoginMethod>("email");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [platform, setPlatform] = useState("YouTube");
  const [handle, setHandle] = useState("");
  const [followers, setFollowers] = useState("");
  const [country, setCountry] = useState("");
  const [bio, setBio] = useState("");
  const [photoPreview, setPhotoPreview] = useState("");

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setPhotoPreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleLogin = () => {
    setError("");
    const identifier = loginMethod === "email" ? loginEmail : loginPhone;
    if (!identifier || !loginPassword) { setError("Please fill in all fields."); return; }
    setLoading(true);
    setTimeout(() => {
      const found = mockAccounts.find((a) =>
        loginMethod === "email" ? a.email === identifier : a.phone === identifier
      );
      if (found && loginPassword === "password123") {
        onSuccess(found);
      } else {
        setError(
          loginMethod === "email"
            ? "Invalid email or password. Try: alex@example.com / password123"
            : "Invalid phone or password. Try: +1 555 000 0001 / password123"
        );
      }
      setLoading(false);
    }, 900);
  };

  const handleSignupStep1 = () => {
    setError("");
    if (!name) { setError("Please enter your full name."); return; }
    if (signupMethod === "email" && !email) { setError("Please enter your email address."); return; }
    if (signupMethod === "phone" && !phone) { setError("Please enter your phone number."); return; }
    if (!password) { setError("Please enter a password."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirmPassword) { setError("Passwords do not match."); return; }
    setStep(2);
  };

  const handleSignupStep2 = () => {
    setError("");
    if (!platform || !handle || !followers || !country) { setError("Please fill in all required fields."); return; }
    setLoading(true);
    setTimeout(() => {
      const newUser: StarUser = {
        id: "star-new-" + Date.now(),
        name,
        email: signupMethod === "email" ? email : "",
        phone: signupMethod === "phone" ? phone : "",
        platform,
        platformHandle: handle,
        followers,
        country,
        bio,
        photo: photoPreview || "https://readdy.ai/api/search-image?query=professional%20influencer%20content%20creator%20portrait%20clean%20studio%20background%20modern%20style&width=300&height=300&seq=newstar&orientation=squarish",
        commissionRate: 12,
        verified: false,
        joinedDate: "Apr 2026",
        totalEarned: 0,
        totalSold: 0,
      };
      onSuccess(newUser);
      setLoading(false);
    }, 1000);
  };

  const switchMode = (newMode: "login" | "signup") => {
    setMode(newMode);
    setStep(1);
    setError("");
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="relative bg-gradient-to-r from-amber-400 to-orange-400 p-6 text-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full">
              <i className="ri-user-star-line text-xl"></i>
            </div>
            <div>
              <h2 className="text-xl font-extrabold">{mode === "login" ? "Star Login" : "Join as a Star"}</h2>
              <p className="text-white/80 text-xs">
                {mode === "login" ? "Access your commission dashboard" : "Start earning commissions today"}
              </p>
            </div>
          </div>
          {mode === "signup" && (
            <div className="flex items-center gap-3 mt-4">
              {[1, 2].map((s) => (
                <div key={s} className={`flex items-center gap-1.5 text-xs font-semibold ${step >= s ? "text-white" : "text-white/40"}`}>
                  <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${step >= s ? "bg-white text-amber-500" : "bg-white/20 text-white"}`}>
                    {step > s ? <i className="ri-check-line text-xs"></i> : s}
                  </div>
                  <span>{s === 1 ? "Account Info" : "Social Profile"}</span>
                  {s < 2 && <i className="ri-arrow-right-s-line text-white/50"></i>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-xl px-4 py-3 mb-4 flex items-center gap-2">
              <i className="ri-error-warning-line shrink-0"></i> {error}
            </div>
          )}

          {/* ── LOGIN ── */}
          {mode === "login" && (
            <div className="flex flex-col gap-4">
              {/* Login method toggle */}
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-2">Login with</p>
                <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
                  {(["email", "phone"] as LoginMethod[]).map((m) => (
                    <button
                      key={m}
                      onClick={() => setLoginMethod(m)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap capitalize ${
                        loginMethod === m ? "bg-white text-amber-500" : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <i className={m === "email" ? "ri-mail-line" : "ri-phone-line"}></i>
                      {m === "email" ? "Email" : "Phone Number"}
                    </button>
                  ))}
                </div>
              </div>

              {loginMethod === "email" ? (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email Address</label>
                  <input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Phone Number</label>
                  <div className="flex gap-2">
                    <div className="flex items-center gap-1.5 border border-gray-200 rounded-xl px-3 py-2.5 bg-gray-50 shrink-0">
                      <i className="ri-phone-line text-gray-400 text-sm"></i>
                      <span className="text-sm text-gray-500">+</span>
                    </div>
                    <input
                      type="tel"
                      value={loginPhone}
                      onChange={(e) => setLoginPhone(e.target.value)}
                      placeholder="+1 555 000 0001"
                      className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Password</label>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                />
              </div>

              <p className="text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
                Demo: <strong>alex@example.com</strong> or <strong>+1 555 000 0001</strong> / password: <strong>password123</strong>
              </p>

              <button
                onClick={handleLogin}
                disabled={loading}
                className="w-full bg-amber-400 hover:bg-amber-500 disabled:opacity-60 text-white font-bold py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i> Logging in...
                  </span>
                ) : "Login to Dashboard"}
              </button>

              <p className="text-center text-sm text-gray-500">
                Don&apos;t have an account?{" "}
                <button onClick={() => switchMode("signup")} className="text-amber-500 font-semibold hover:underline cursor-pointer">
                  Sign Up Free
                </button>
              </p>
            </div>
          )}

          {/* ── SIGNUP STEP 1 ── */}
          {mode === "signup" && step === 1 && (
            <div className="flex flex-col gap-4">
              {/* Signup method toggle */}
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-2">Sign up with</p>
                <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
                  {(["email", "phone"] as LoginMethod[]).map((m) => (
                    <button
                      key={m}
                      onClick={() => setSignupMethod(m)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                        signupMethod === m ? "bg-white text-amber-500" : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <i className={m === "email" ? "ri-mail-line" : "ri-phone-line"}></i>
                      {m === "email" ? "Email" : "Phone Number"}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Full Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your full name"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                />
              </div>

              {signupMethod === "email" ? (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email Address *</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Phone Number *</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 555 123 4567"
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Password *</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 6 characters"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Confirm Password *</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                />
              </div>

              <button
                onClick={handleSignupStep1}
                className="w-full bg-amber-400 hover:bg-amber-500 text-white font-bold py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                Continue <i className="ri-arrow-right-line ml-1"></i>
              </button>

              <p className="text-center text-sm text-gray-500">
                Already have an account?{" "}
                <button onClick={() => switchMode("login")} className="text-amber-500 font-semibold hover:underline cursor-pointer">
                  Login
                </button>
              </p>
            </div>
          )}

          {/* ── SIGNUP STEP 2 ── */}
          {mode === "signup" && step === 2 && (
            <div className="flex flex-col gap-4">
              {/* Photo upload */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-100 shrink-0 flex items-center justify-center">
                  {photoPreview
                    ? <img src={photoPreview} alt="preview" className="w-full h-full object-cover" />
                    : <i className="ri-user-line text-2xl text-gray-300"></i>
                  }
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Profile Photo</label>
                  <label className="flex items-center gap-2 text-xs bg-amber-50 text-amber-600 font-semibold px-3 py-2 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors whitespace-nowrap">
                    <i className="ri-upload-2-line"></i> Upload Photo
                    <input type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
                  </label>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG up to 5MB</p>
                </div>
              </div>

              {/* Platform */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Primary Platform *</label>
                <div className="grid grid-cols-4 gap-2">
                  {platformOptions.map((p) => (
                    <button
                      key={p}
                      onClick={() => setPlatform(p)}
                      className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border-2 text-xs font-semibold transition-colors cursor-pointer ${
                        platform === p ? "border-amber-400 bg-amber-50 text-amber-600" : "border-gray-200 text-gray-500 hover:border-amber-300"
                      }`}
                    >
                      <i className={`${platformIcons[p]} text-lg`}></i>
                      <span>{p === "Twitter/X" ? "Twitter" : p}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">@Handle / Channel *</label>
                  <input
                    type="text"
                    value={handle}
                    onChange={(e) => setHandle(e.target.value)}
                    placeholder="@YourHandle"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Followers Count *</label>
                  <input
                    type="text"
                    value={followers}
                    onChange={(e) => setFollowers(e.target.value)}
                    placeholder="e.g. 500K"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Country *</label>
                <input
                  type="text"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  placeholder="Your country"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Bio <span className="text-gray-400 font-normal">(optional)</span></label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Tell us about your content and audience..."
                  rows={3}
                  maxLength={300}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors resize-none"
                />
                <p className="text-xs text-gray-400 mt-1">{bio.length}/300</p>
              </div>

              <div className="bg-amber-50 rounded-xl p-3 flex items-start gap-2">
                <i className="ri-information-line text-amber-500 shrink-0 mt-0.5"></i>
                <p className="text-xs text-amber-700">New stars start at <strong>12% commission</strong>. Your rate increases based on sales performance and follower count.</p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 border border-gray-200 text-gray-600 font-semibold py-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Back
                </button>
                <button
                  onClick={handleSignupStep2}
                  disabled={loading}
                  className="flex-1 bg-amber-400 hover:bg-amber-500 disabled:opacity-60 text-white font-bold py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i> Creating...
                    </span>
                  ) : "Create Account"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
